import React from "react";
import styled from "styled-components";
import { Card, Empty, Progress, Space, Tag, Typography } from "antd";

const { Text } = Typography;

const WidgetCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;

  .ant-card-head-title {
    color: #005249;
    font-weight: 700;
  }
`;

const Section = styled.div`
  display: grid;
  gap: 10px;
`;

const Row = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 10px;
`;

const RowHeader = styled(Space)`
  width: 100%;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const SourceCampaignWidget = ({ sourceCampaign }) => {
  if (!hasAttributionData(sourceCampaign)) {
    return (
      <WidgetCard title="Source and Campaign">
        <Empty
          description="No source or campaign performance data for the selected filters"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </WidgetCard>
    );
  }

  return (
    <WidgetCard
      title="Source and Campaign"
      extra={<Tag color="blue">{sourceCampaign.sources.length} sources</Tag>}
      data-testid="source-campaign-widget"
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <AttributionSection title="Top Sources" rows={sourceCampaign.sources} />
        <AttributionSection title="Top Campaigns" rows={sourceCampaign.campaigns} />
        {sourceCampaign.attributionNote && (
          <Text type="secondary">{sourceCampaign.attributionNote}</Text>
        )}
      </Space>
    </WidgetCard>
  );
};

const AttributionSection = ({ title, rows }) => {
  if (!rows?.length) return null;

  const maxLeads = Math.max(...rows.map((row) => Number(row.leads || 0)), 1);

  return (
    <Section>
      <Text strong>{title}</Text>
      {rows.map((row) => (
        <Row key={row.drilldownKey} data-drilldown-key={row.drilldownKey}>
          <RowHeader>
            <Text strong>{row.label}</Text>
            <Text>{formatNumber(row.leads)} leads</Text>
          </RowHeader>
          <Progress
            percent={Math.round((Number(row.leads || 0) / maxLeads) * 100)}
            showInfo={false}
            strokeColor="#005249"
          />
          <Space size={[6, 6]} wrap>
            <Tag>{formatNumber(row.qualifiedLeads)} qualified</Tag>
            <Tag>{formatNumber(row.quotesGenerated)} quotes</Tag>
            <Tag color={row.ordersCreated > 0 ? "green" : "default"}>
              {row.conversionRate}% conversion
            </Tag>
            {row.attributedValue > 0 && (
              <Tag color="gold">{formatCurrency(row.attributedValue)}</Tag>
            )}
          </Space>
        </Row>
      ))}
    </Section>
  );
};

function hasAttributionData(sourceCampaign) {
  return Boolean(sourceCampaign?.sources?.length || sourceCampaign?.campaigns?.length);
}

function formatNumber(value) {
  return new Intl.NumberFormat("en-ZA").format(Number(value || 0));
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

export default SourceCampaignWidget;
